/**
 * Passkey (WebAuthn) Service
 *
 * Provides functions for passkey registration and authentication.
 * Handles the full WebAuthn ceremony including:
 * - Getting options from the server
 * - Calling the browser WebAuthn API
 * - Verifying the result with the server
 */

import api from './api';
import type { LoginResponse } from '@/types';
import type {
  Passkey,
  PasskeyListResponse,
  PasskeyRegistrationOptionsResponse,
  PasskeyRegistrationVerifyResponse,
  PasskeyAuthOptionsResponse,
} from '@/types/security';
import {
  isWebAuthnSupported,
  parseCreationOptions,
  parseRequestOptions,
  serializeRegistrationCredential,
  serializeAuthenticationCredential,
  parseWebAuthnError,
  detectDeviceName,
  refineDeviceName,
} from '@/utils/webauthn';

// ============================================================================
// Registration
// ============================================================================

/**
 * Register a new passkey for the current user
 * Performs the full WebAuthn registration ceremony:
 * 1. Get registration options from server
 * 2. Call browser WebAuthn API
 * 3. Send result to server for verification
 *
 * @param deviceName Optional device name (auto-detected if not provided)
 * @returns The registered passkey information
 * @throws WebAuthnError if the operation fails
 */
export async function registerPasskey(
  deviceName?: string
): Promise<PasskeyRegistrationVerifyResponse> {
  // Check browser support
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser.');
  }

  // 1. Get registration options from server
  const optionsResponse = await api.post<PasskeyRegistrationOptionsResponse>(
    '/auth/mfa/passkey/register/options'
  );

  // 2. Parse options and call WebAuthn API
  const publicKeyOptions = parseCreationOptions(optionsResponse.options);

  let credential: PublicKeyCredential;
  try {
    const result = await navigator.credentials.create({
      publicKey: publicKeyOptions,
    });

    if (!result || !(result instanceof PublicKeyCredential)) {
      throw new Error('Passkey registration was cancelled or failed.');
    }

    credential = result;
  } catch (error) {
    throw parseWebAuthnError(error);
  }

  // 3. Determine device name
  const finalDeviceName =
    deviceName ||
    refineDeviceName(credential.authenticatorAttachment as AuthenticatorAttachment | null | undefined) ||
    detectDeviceName();

  // 4. Serialize credential and send to server
  const serializedCredential = serializeRegistrationCredential(credential);

  // Note: Backend expects snake_case for these fields
  const verifyResponse = await api.post<PasskeyRegistrationVerifyResponse>(
    '/auth/mfa/passkey/register/verify',
    {
      challenge_id: optionsResponse.challengeId,
      credential: serializedCredential,
      device_name: finalDeviceName,
    }
  );

  return verifyResponse;
}

// ============================================================================
// Authentication
// ============================================================================

/**
 * Authenticate with a passkey
 * Performs the full WebAuthn authentication ceremony:
 * 1. Get authentication options from server
 * 2. Call browser WebAuthn API
 * 3. Send result to server for verification
 *
 * @param email Optional email for discoverable credentials
 * @returns Authentication tokens on success
 * @throws WebAuthnError if the operation fails
 */
export async function authenticateWithPasskey(
  email?: string
): Promise<LoginResponse> {
  // Check browser support
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser.');
  }

  // 1. Get authentication options from server
  const optionsResponse = await api.post<PasskeyAuthOptionsResponse>(
    '/auth/mfa/passkey/authenticate/options',
    email ? { email } : undefined
  );

  // 2. Parse options and call WebAuthn API
  const publicKeyOptions = parseRequestOptions(optionsResponse.options);

  let credential: PublicKeyCredential;
  try {
    const result = await navigator.credentials.get({
      publicKey: publicKeyOptions,
    });

    if (!result || !(result instanceof PublicKeyCredential)) {
      throw new Error('Passkey authentication was cancelled or failed.');
    }

    credential = result;
  } catch (error) {
    throw parseWebAuthnError(error);
  }

  // 3. Serialize credential and send to server
  const serializedCredential = serializeAuthenticationCredential(credential);

  const verifyResponse = await api.post<LoginResponse>(
    '/auth/mfa/passkey/authenticate/verify',
    {
      challenge_id: optionsResponse.challengeId,
      credential: serializedCredential,
    }
  );

  return verifyResponse;
}

/**
 * Authenticate with a passkey during MFA challenge
 * Similar to authenticateWithPasskey but uses the MFA challenge flow
 *
 * @param mfaToken The MFA token from the initial login
 * @returns Authentication tokens on success
 * @throws WebAuthnError if the operation fails
 */
export async function authenticateWithPasskeyMfa(
  mfaToken: string
): Promise<LoginResponse> {
  // Check browser support
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported in this browser.');
  }

  // 1. Get authentication options from server with MFA token
  const optionsResponse = await api.post<PasskeyAuthOptionsResponse>(
    '/auth/mfa/challenge/passkey/options',
    { mfa_token: mfaToken }
  );

  // 2. Parse options and call WebAuthn API
  const publicKeyOptions = parseRequestOptions(optionsResponse.options);

  let credential: PublicKeyCredential;
  try {
    const result = await navigator.credentials.get({
      publicKey: publicKeyOptions,
    });

    if (!result || !(result instanceof PublicKeyCredential)) {
      throw new Error('Passkey authentication was cancelled or failed.');
    }

    credential = result;
  } catch (error) {
    throw parseWebAuthnError(error);
  }

  // 3. Serialize credential and send to server
  const serializedCredential = serializeAuthenticationCredential(credential);

  const verifyResponse = await api.post<LoginResponse>(
    '/auth/mfa/challenge/passkey/verify',
    {
      mfa_token: mfaToken,
      credential: serializedCredential,
    }
  );

  return verifyResponse;
}

// ============================================================================
// Passkey Management
// ============================================================================

/**
 * List all passkeys for the current user
 * @returns Array of registered passkeys
 */
export async function listPasskeys(): Promise<Passkey[]> {
  const response = await api.get<PasskeyListResponse>('/auth/mfa/passkeys');
  return response.passkeys;
}

/**
 * Delete a passkey
 * @param passkeyId The ID of the passkey to delete
 */
export async function deletePasskey(passkeyId: string): Promise<void> {
  await api.delete(`/auth/mfa/passkey/${passkeyId}`);
}

// ============================================================================
// Export Service Object
// ============================================================================

export const passkeyApi = {
  registerPasskey,
  authenticateWithPasskey,
  authenticateWithPasskeyMfa,
  listPasskeys,
  deletePasskey,
};

export default passkeyApi;
